// src/pages/EditDealerMarketingSupport.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface Report {
  id: number;
  created_at: string;
}

interface MarketingSupportData {
  marketingMaterials: string;
  outreachChannels: string;
  additionalSupport: string;
}

const EditDealerMarketingSupport: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // Dealer id from route
  const navigate = useNavigate();

  // State for marketing support data form.
  const [formData, setFormData] = useState<MarketingSupportData>({
    marketingMaterials: "",
    outreachChannels: "",
    additionalSupport: ""
  });
  
  // State to store record id (if exists) and report integration.
  const [recordId, setRecordId] = useState<number | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [newestReportId, setNewestReportId] = useState<number | null>(null);

  // Step 1: Fetch the dealer's reports. If none exist, create one.
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then((res) => res.json())
      .then((data: Report[]) => {
        if (Array.isArray(data) && data.length > 0) {
          // Assume reports are sorted descending by created_at.
          setReports(data);
          setNewestReportId(data[0].id);
        } else {
          // No report exists; create one.
          fetch(`/api/dealers/${id}/reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" }
          })
            .then((res) => res.json())
            .then((newReport) => setNewestReportId(newReport.id))
            .catch((err) => console.error("Error creating report", err));
        }
      })
      .catch((err) => console.error("Error fetching reports", err));
  }, [id]);

  // Step 2: With newestReportId available, fetch dealer marketing support data.
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/dealer-marketing-support`)
      .then((res) => res.json())
      .then((data) => {
        setFormData({
          marketingMaterials: data.marketing_materials || "",
          outreachChannels: data.outreach_channels || "",
          additionalSupport: data.additional_support || ""
        });
        if (data.id) {
          setRecordId(data.id);
        }
      })
      .catch((err) => console.error("Error fetching dealer marketing support", err));
  }, [newestReportId, id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Step 3: On form submission, update via report‑centric endpoint.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Build the payload. Include dealer_id for a POST.
    const payload = {
      dealer_id: parseInt(id!, 10),
      marketingMaterials: formData.marketingMaterials,
      outreachChannels: formData.outreachChannels,
      additionalSupport: formData.additionalSupport
    };

    if (newestReportId) {
      if (recordId) {
        // Update existing record.
        fetch(`/api/reports/${newestReportId}/dealer-marketing-support`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: recordId, ...payload })
        })
          .then(() => navigate(`/dealer/${id}`))
          .catch((err) => console.error("Error updating dealer marketing support", err));
      } else {
        // Create a new record.
        fetch(`/api/reports/${newestReportId}/dealer-marketing-support`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })
          .then(() => navigate(`/dealer/${id}`))
          .catch((err) => console.error("Error creating dealer marketing support", err));
      }
    } else {
      console.error("No report found for this dealer.");
    }
  };

  // Styling objects remain unchanged.
  const containerStyle: React.CSSProperties = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" };
  const cardStyle: React.CSSProperties = { backgroundColor: "#FFFFFF", borderRadius: "8px", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", maxWidth: "800px", margin: "30px auto", padding: "30px" };
  const headerStyle: React.CSSProperties = { textAlign: "center", color: "#e41e26", borderBottom: "2px solid #e41e26", paddingBottom: "10px", marginBottom: "20px", fontSize: "24px" };
  const labelStyle: React.CSSProperties = { display: "block", marginBottom: "5px", fontWeight: "bold" };
  const inputStyle: React.CSSProperties = { width: "100%", padding: "8px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc" };
  const buttonStyle: React.CSSProperties = { padding: "5px 10px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "10px" };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Dealer Marketing Support</h1>
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Marketing Materials:</label>
          <textarea
            name="marketingMaterials"
            style={inputStyle}
            value={formData.marketingMaterials}
            onChange={handleChange}
            placeholder="Enter marketing materials details"
          />
          <label style={labelStyle}>Outreach Channels:</label>
          <textarea
            name="outreachChannels"
            style={inputStyle}
            value={formData.outreachChannels}
            onChange={handleChange}
            placeholder="List outreach channels"
          />
          <label style={labelStyle}>Additional Support:</label>
          <textarea
            name="additionalSupport"
            style={inputStyle}
            value={formData.additionalSupport}
            onChange={handleChange}
            placeholder="Describe additional support needed"
          />
          <div style={{ marginTop: "20px" }}>
            <button type="submit" style={buttonStyle}>
              Save
            </button>
            <button type="button" style={buttonStyle} onClick={() => navigate(`/dealer/${id}`)}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDealerMarketingSupport;
